<template>
  <RevForm :initial-values :validate @submit="onSubmit">
    <template #default="{ values, errors }">
      <div class="flex gap-8" :class="wrapperClasses">
        <RevInputText
          id="email-collector"
          v-model="values.email"
          class="w-full"
          :error="errors.email"
          helper-text-class="mt-8 !text-static-default-hi"
          :icon="IconMail"
          :label="placeholderLabel"
          type="email"
        />

        <CmsButtonBase
          class="!h-fit"
          :class="buttonClasses"
          :disabled="submitButtonIsDisabled"
          :loading="isLoading"
          type="submit"
          variant="primary"
          width="adaptive"
        >
          {{ buttonLabel }}
        </CmsButtonBase>
      </div>
    </template>
  </RevForm>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { Tracking } from '@backmarket/http-api/src/api-specs-content/models/tracking'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevForm, makeValidate, required } from '@ds/components/Form'
import { RevInputText } from '@ds/components/InputText'
import { IconMail } from '@ds/icons/IconMail'

import CmsButtonBase from '../../shared-components/CmsButtonBase/CmsButtonBase.vue'

import translations from './EmailCollector.translations'
import type { EmailSource } from './EmailCollector.types'
import { useCollectEmail } from './useCollectEmail'

const props = defineProps<{
  tracking: Tracking
  placeholderLabel: string
  submitButtonLabel?: string
  toastText?: { successTitle: string; successMessage: string }
  wrapperClasses?: string
  buttonClasses?: string
  source: EmailSource
}>()

const i18n = useI18n()
const { collectEmail, isLoading, submitButtonIsDisabled, emailCollected } =
  useCollectEmail()

const emit = defineEmits(['submitted'])

const initialValues = { email: '' }

const validate = makeValidate<typeof initialValues>({
  email: required(i18n(translations.formMandatoryError)),
})

const buttonLabel = computed(() => {
  return props.submitButtonLabel || i18n(translations.submitButtonLabel)
})

async function onSubmit(values: typeof initialValues) {
  await collectEmail({
    email: values.email as string,
    trackingData: props.tracking,
    toastText: props.toastText,
    source: props.source,
  })

  if (emailCollected.value) emit('submitted')
}
</script>
